import React, { useState, useCallback } from 'react';
import { 
    Box, Typography, Container, Paper, IconButton,
    Grid, Button, Card, CardContent, Divider, 
    Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, AlertTitle, Tooltip, useTheme
} from '@mui/material';
import { 
    Key as KeyIcon, 
    RotateRight as RotateRightIcon, 
    Block as BlockIcon, 
    CloudDone as CloudDoneIcon,
    Warning as WarningIcon,
    Lock as LockIcon,
     
    Refresh as RefreshIcon
} from '@mui/icons-material';
import moment from 'moment';

// --- MOCK KEY DATA ---
const mockKeys = [
    { 
        id: 'KEY_001_DATA_AES256', 
        name: 'Database Encryption Key', 
        algorithm: 'AES-256-GCM', 
        status: 'Active', 
        created: '2025-01-15T10:00:00Z', 
        rotated: '2025-07-15T10:00:00Z',
        nextRotation: '2026-01-15T10:00:00Z',
        type: 'Symmetric',
        usage: 'Primary Data Encryption'
    },
    { 
        id: 'KEY_002_SESSION_RSA4096', 
        name: 'Session Signing Key', 
        algorithm: 'RSA-4096', 
        status: 'Active', 
        created: '2025-09-01T08:00:00Z', 
        rotated: '2025-09-01T08:00:00Z',
        nextRotation: '2025-12-01T08:00:00Z',
        type: 'Asymmetric',
        usage: 'JWT Signing'
    },
    { 
        id: 'KEY_003_LEGACY_DES', 
        name: 'Legacy Archive Key', 
        algorithm: 'DES', 
        status: 'Deprecated', 
        created: '2020-03-20T12:00:00Z', 
        rotated: '2020-03-20T12:00:00Z',
        nextRotation: 'N/A',
        type: 'Symmetric',
        usage: 'Old Data Archives'
    },
];

// --- HELPER FUNCTIONS ---

const getKeyStatusColor = (status, theme) => {
    switch (status) {
        case 'Active': return theme.palette.success.main;
        case 'Deprecated': return theme.palette.warning.main;
        case 'Revoked': return theme.palette.error.main;
        default: return theme.palette.info.main;
    }
};

// --- KEY CARD COMPONENT ---
const KeyCard = ({ keyData, onRotate, onRevoke }) => {
    const theme = useTheme();
    const [openDialog, setOpenDialog] = useState(null); // 'rotate' or 'revoke'
    
    const isDeprecated = keyData.status === 'Deprecated';
    const isDueForRotation = keyData.nextRotation && moment().isAfter(moment(keyData.nextRotation).subtract(30, 'days'));
    const statusColor = getKeyStatusColor(keyData.status, theme);

    // Calculate days until next rotation for the tooltip
    const daysUntilRotation = keyData.nextRotation 
        ? moment(keyData.nextRotation).diff(moment(), 'days') 
        : null;

    const handleConfirm = () => {
        if (openDialog === 'rotate') {
            onRotate(keyData.id);
        } else if (openDialog === 'revoke') {
            onRevoke(keyData.id);
        }
        setOpenDialog(null);
    };

    return (
        <Card elevation={4} sx={{ borderLeft: `5px solid ${statusColor}`, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LockIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>
                            {keyData.type} - {keyData.algorithm}
                        </Typography>
                    </Box>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            backgroundColor: statusColor, 
                            color: 'white', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1, 
                            fontWeight: 700 
                        }}
                    >
                        {keyData.status.toUpperCase()}
                    </Typography>
                </Box>
                
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, mb: 0.5, color: theme.palette.primary.dark }}>
                    {keyData.name}
                </Typography>
                <Typography variant="body2" color="textPrimary" sx={{ mb: 2 }}>
                    **Usage:** {keyData.usage}
                </Typography>

                <Divider sx={{ my: 1 }} />
                
                <Grid container spacing={1} sx={{ fontSize: '0.85rem' }}>
                    <Grid item xs={12} sm={6}>
                        **ID:** <code style={{ color: theme.palette.info.dark }}>{keyData.id}</code>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        **Created:** {moment(keyData.created).format('YYYY-MM-DD')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        **Last Rotated:** {moment(keyData.rotated).format('YYYY-MM-DD HH:mm')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            **Next Rotation:** <Tooltip title={`Key should be rotated in ${daysUntilRotation} days.`} arrow>
                                <Typography variant="body2" component="span" sx={{ ml: 0.5, fontWeight: isDueForRotation ? 700 : 400, color: isDueForRotation ? theme.palette.error.dark : 'inherit' }}>
                                    {keyData.nextRotation ? moment(keyData.nextRotation).format('YYYY-MM-DD') : 'N/A'}
                                </Typography>
                            </Tooltip>
                            {isDueForRotation && <WarningIcon color="error" sx={{ ml: 0.5, fontSize: '1rem' }} />}
                        </Box>
                    </Grid>
                </Grid>

            </CardContent>
            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Tooltip title={isDeprecated ? "Cannot rotate deprecated keys." : ""}>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        startIcon={<RotateRightIcon />}
                        onClick={() => setOpenDialog('rotate')}
                        disabled={keyData.status !== 'Active'}
                        sx={{ mr: 1 }}
                    >
                        Rotate
                    </Button>
                </Tooltip>
                
                <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<BlockIcon />}
                    onClick={() => setOpenDialog('revoke')}
                    disabled={keyData.status === 'Revoked'}
                >
                    Revoke
                </Button>
            </Box>

            {/* Confirmation Dialogs (Functional UX) */}
            <Dialog open={!!openDialog} onClose={() => setOpenDialog(null)}>
                <DialogTitle sx={{ color: openDialog === 'rotate' ? theme.palette.secondary.main : theme.palette.error.main }}>
                    {openDialog === 'rotate' ? `Confirm Key Rotation` : `Confirm Key Revocation`}
                </DialogTitle>
                <DialogContent>
                    <Alert severity={openDialog === 'rotate' ? 'warning' : 'error'} sx={{ mb: 2 }}>
                        <AlertTitle>{openDialog === 'rotate' ? 'Downtime Warning' : 'Permanent Action'}</AlertTitle>
                        This is a highly sensitive action. Please ensure all dependent services are prepared to switch to a new key. {keyData.name} **({keyData.id})** will be affected.
                    </Alert>
                    <Typography>
                        Are you absolutely sure you want to **{openDialog}** this key? This operation is **irreversible** for data encrypted with the key post-revocation.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(null)} color="primary">Cancel</Button>
                    <Button onClick={handleConfirm} color={openDialog === 'rotate' ? 'secondary' : 'error'} variant="contained" autoFocus>
                        Yes, {openDialog === 'rotate' ? 'Rotate Key' : 'Revoke Key'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
};

// --- MAIN COMPONENT ---
const EncryptionKeyMgmt = () => {
    const theme = useTheme();
    const [keys, setKeys] = useState(mockKeys);

    const handleRotate = useCallback((keyId) => {
        setKeys(prevKeys => prevKeys.map(key => 
            key.id === keyId 
                ? { 
                    ...key, 
                    rotated: moment().toISOString(), 
                    nextRotation: moment().add(6, 'months').toISOString(),
                    status: 'Active' // Ensure status is active after rotation
                } 
                : key
        ));
        // Add API call here
        alert(`Successfully rotated key: ${keyId}`);
    }, []);

    const handleRevoke = useCallback((keyId) => {
        setKeys(prevKeys => prevKeys.map(key => 
            key.id === keyId ? { ...key, status: 'Revoked' } : key
        ));
        // Add API call here
        alert(`Successfully revoked key: ${keyId}`);
    }, []);

    const activeKeys = keys.filter(k => k.status === 'Active' || k.status === 'Deprecated');
    const revokedKeys = keys.filter(k => k.status === 'Revoked');

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom 
                sx={{ 
                    fontWeight: 800, 
                    color: theme.palette.primary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                }}
            >
                <KeyIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.dark }} />
                Encryption Key Management
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Manage the lifecycle (rotation, revocation) of cryptographic keys for system security.
            </Typography>

            <Alert severity="error" sx={{ mb: 4, py: 1.5, fontWeight: 600 }}>
                <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                **Critical Security Warning:** Two keys are due for rotation within 90 days. Please review and rotate immediately.
            </Alert>

            <Paper elevation={6} sx={{ p: 4, borderRadius: 2 }}>
                
                {/* Active Keys Section */}
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CloudDoneIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="h5" fontWeight={700} color="success.dark">Active & Deprecated Keys ({activeKeys.length})</Typography>
                        <Tooltip title="Manually refresh key list">
                            <IconButton color="info" size="small" sx={{ ml: 1 }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={3}>
                        {activeKeys.map(key => (
                            <Grid item xs={12} lg={6} key={key.id}>
                                <KeyCard keyData={key} onRotate={handleRotate} onRevoke={handleRevoke} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                
                {/* Revoked Keys Section */}
                {revokedKeys.length > 0 && (
                    <Box sx={{ mt: 5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BlockIcon color="error" sx={{ mr: 1 }} />
                            <Typography variant="h5" fontWeight={700} color="error.dark">Revoked Keys ({revokedKeys.length})</Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                            {revokedKeys.map(key => (
                                <Grid item xs={12} lg={6} key={key.id}>
                                    <KeyCard keyData={key} onRotate={handleRotate} onRevoke={handleRevoke} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default EncryptionKeyMgmt;